import React from 'react';
import { ShieldCheck, KeyRound, Database, ShieldAlert } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';

const Security = () => {
  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: 'Data Encryption',
      description: 'Your first line of defense with 128-bit encryption'
    },
    {
      icon: KeyRound,
      title: 'Two-Factor Authentication',
      description: 'An extra layer of security for your account'
    },
    {
      icon: Database,
      title: 'Secure Backup',
      description: 'Protect against data loss with automatic backups'
    },
    {
      icon: ShieldAlert,
      title: 'Firewall Protection',
      description: 'Guarding your digital perimeter 24/7'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#40E095] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#40E095] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-block bg-[#40E095]/20 text-[#40E095] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Enterprise-Grade Security
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Built For <span className="text-[#40E095]">Trusted Privacy</span>
              </h2>
              <p className="text-lg text-gray-300">
                Your data security is our top priority. We employ multiple layers of protection 
                to ensure your files remain private and secure at all times.
              </p>
            </div>

            {/* Security Stats */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <p className="text-4xl font-bold text-[#40E095] mb-2">95%</p>
              <p className="text-gray-300">
                of cloud breaches are due to human error, not technology. 
                We protect you from both.
              </p>
            </div>

            {/* Security Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b" 
                alt="Security" 
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Right Content - Security Features */}
          <div className="space-y-6">
            {securityFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300 hover:border-[#40E095]/50 group"
                >
                  <CardHeader className="flex flex-row items-start space-x-4">
                    <div className="bg-[#40E095]/20 group-hover:bg-[#40E095] p-4 rounded-xl transition-all">
                      <IconComponent className="text-[#40E095] group-hover:text-white transition-colors" size={28} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-white text-xl mb-2">
                        {feature.title}
                      </CardTitle>
                      <CardDescription className="text-gray-300">
                        {feature.description}
                      </CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}

            {/* Additional Security Info */}
            <div className="bg-[#40E095]/10 border border-[#40E095]/30 rounded-2xl p-6 mt-8">
              <h4 className="font-bold text-white mb-3 flex items-center">
                <ShieldCheck className="mr-2 text-[#40E095]" size={24} />
                Private by Design
              </h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                We use end-to-end encryption for sensitive folders, ensuring that only you 
                can access your data. Not even we can see your files.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Security;
